import { format } from "date-fns";
import { Calendar } from "lucide-react";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { ActionItem, ActionItemStatus } from "../../../types/actionItems";
import { Input } from "../../ui/input";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "../../shared/StatusBadge";
import React from "react";

interface ActionItemMetadataProps {
  item: ActionItem & { meeting?: { title: string } | null };
  onStatusChange: (status: ActionItemStatus) => void;
  onDueDateChange: (date: string) => void;
}

const ACTION_ITEM_STATUSES: { value: ActionItemStatus; label: string }[] = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "done", label: "Done" }
];

export function ActionItemMetadata({ item, onStatusChange, onDueDateChange }: ActionItemMetadataProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-[26px]">
        <span className="text-sm text-gray-500">Status:</span>
        <Select value={item.status} onValueChange={onStatusChange}>
          <SelectTrigger className="w-[200px] h-8 border-gray-200">
            <StatusBadge status={item.status} />
          </SelectTrigger>
          <SelectContent>
            {ACTION_ITEM_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <StatusBadge status={status.value} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-500">Due Date:</span>
        <div className="relative flex items-center">
          <Input
            type="datetime-local"
            value={item.due_date ? new Date(item.due_date).toISOString().slice(0, 16) : ''}
            onChange={(e) => onDueDateChange(e.target.value)}
            className="h-7 pl-2 py-1 border-gray-200 text-sm text-gray-500"
          />
        </div>
      </div>

      {item.meeting_id && item.meeting?.title && (
        <div className="flex items-start gap-1.5">
          <span className="text-sm text-gray-500">Meeting:</span>
          <button
            onClick={() => navigate(`/app/meeting/${item.meeting_id}`)}
            className="text-sm text-primary hover:text-primary/80 text-left break-words"
          >
            {item.meeting.title}
          </button>
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-500">Created Date:</span>
        <span className="text-sm text-gray-500">
          {format(new Date(item.created_at), 'MMMM dd, yyyy h:mmaaa')}
        </span>
      </div>
    </div>
  );
}